import type { CalendarEvent } from '../../../types';
import type { EventVariant } from '../types';
import { createEventCSSVars } from '../utils/createEventCSSVars';
import { getEventVariant } from '../utils/getEventVariant';

import { useEventColorTreatment } from './useEventColorTreatment';

/**
 * Hook that encapsulates the complete event styling pipeline:
 * 1. Determines event variant (standard/actionable/block) and border style
 * 2. Resolves color treatment from theme based on appointment state
 * 3. Generates CSS variables for the event's styling
 *
 * This hook provides a clean interface for EventComponent while preserving
 * the testability of the underlying pure functions (getEventVariant, createEventCSSVars).
 *
 * Note: Action messages are handled separately by useActionMessage hook.
 *
 * @param event - Calendar event to style
 * @returns Styling configuration (variant and CSS variables)
 *
 * @example
 * const { variant, cssVars } = useEventStyling(event);
 * return <Box style={cssVars}>...</Box>;
 */
export const useEventStyling = (
  event: CalendarEvent,
): {
  variant: EventVariant;
  cssVars: Record<string, string>;
} => {
  // Step 1: Determine variant configuration from event properties
  const { variant, borderStyle } = getEventVariant(event);

  // Step 2: Get color treatment from theme (requires hook for theme access)
  const colorTreatment = useEventColorTreatment(event);

  // Step 3: Create CSS variables (single source of truth for variant styling)
  const cssVars = createEventCSSVars(variant, colorTreatment, borderStyle);

  return { variant, cssVars };
};
